/* v3.99d.388 — Dismissible one-time banner nudging logged-in users to
 * enable browser push notifications for auction alerts (outbid,
 * ending-soon, won). Renders only when:
 *   • Push API is supported on this browser
 *   • Permission state is 'default' (never asked)
 *   • User is authenticated
 *   • Banner not previously dismissed on this device
 *   • Server reports zero active subscriptions for this user
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  isPushSupported, isIosStandaloneRequired, subscribeToPush,
  getPushPermissionState,
} from "../lib/push";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

const DISMISS_KEY = "cf_push_banner_dismissed_v1";

export default function PushEnableBanner() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user || !user.id) return;
      if (!isPushSupported()) return;
      if (isIosStandaloneRequired()) return;   // iOS Safari (non-PWA)
      if (getPushPermissionState() !== "default") return;
      try {
        if (localStorage.getItem(DISMISS_KEY)) return;
      } catch (_e) { /* private mode */ }
      // Only show if server has no active sub for this user.
      try {
        const { data } = await api.get("/push/preferences");
        if (cancelled) return;
        if ((data?.active_subscriptions || 0) === 0) setShow(true);
      } catch (_e2) { /* not authed / offline */ return; }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (_e3) { /* ignore */ }
    setShow(false);
  };

  const enable = async () => {
    setBusy(true);
    const res = await subscribeToPush();
    setBusy(false);
    if (res.ok) {
      toast.success("Notifications enabled — you'll hear about outbids and closes");
      dismiss();
    } else if (res.reason === "denied") {
      toast.error("Permission denied. You can enable it in your browser settings.");
      dismiss();
    } else if (res.reason === "unsupported") {
      dismiss();
    } else {
      toast.error("Couldn't enable notifications (" + res.reason + ")");
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="push-enable-banner"
      className="w-full border-b"
      style={{
        background: "linear-gradient(90deg, rgba(234,88,12,0.10), rgba(234,88,12,0.02))",
        borderColor: "rgba(234,88,12,0.35)",
      }}
    >
      <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3 px-4 py-2">
        <div className="text-xs sm:text-sm flex-1 min-w-[200px]"
             style={{ color: "var(--text)" }}>
          <span className="font-semibold" style={{ color: "#ea580c" }}>
            Get instant auction alerts
          </span>
          <span className="ml-2" style={{ color: "var(--text-2)" }}>
            You&apos;ll be pinged the moment you&apos;re outbid or an auction
            you&apos;re watching is about to end.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="push-enable-banner-enable"
            onClick={enable}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-semibold"
            style={{ background: "#ea580c", color: "#fff",
                     borderRadius: 2, minWidth: 110 }}
          >
            {busy ? "Enabling…" : "Enable"}
          </button>
          <button
            data-testid="push-enable-banner-dismiss"
            onClick={dismiss}
            className="px-3 py-1.5 text-xs border"
            style={{ borderColor: "var(--border)",
                     color: "var(--text-2)", borderRadius: 2 }}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
